
import React, { useState } from 'react';
import { 
  MessageSquare, 
  Heart, 
  Share2, 
  UserPlus, 
  Plus, 
  Search, 
  TreePine, 
  Zap, 
  ShieldCheck, 
  Hammer, 
  Wind,
  Image as ImageIcon,
  Send,
  ArrowUp
} from 'lucide-react';
import { ForumPost, UserProfile, ForumComment } from '../types';

interface ForumProps {
  posts: ForumPost[];
  currentUser: UserProfile;
  onLike: (postId: string) => void;
  onComment: (postId: string, text: string) => void;
  onFollow: (authorId: string) => void;
  onPost: (title: string, content: string, category: ForumPost['category'], imageUrl?: string) => void;
}

export const Forum: React.FC<ForumProps> = ({ posts, currentUser, onLike, onComment, onFollow, onPost }) => {
  const [isPosting, setIsPosting] = useState(false);
  const [sortBy, setSortBy] = useState<'recent' | 'trending'>('recent');

  const sortedPosts = [...posts].sort((a, b) => {
    if (sortBy === 'trending') {
      const aDensity = a.likes.length + a.comments.length;
      const bDensity = b.likes.length + b.comments.length;
      return bDensity - aDensity;
    }
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'BRAGGING_RIGHTS' as ForumPost['category'], imageUrl: '' });
  const [commentingOn, setCommentingOn] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');

  const categories = [
    { id: 'BRAGGING_RIGHTS', label: 'Big Fells', icon: <TreePine className="w-4 h-4" />, color: 'text-emerald-600 bg-emerald-50' },
    { id: 'EQUIPMENT', label: 'Gear Talk', icon: <Hammer className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
    { id: 'SAFETY', label: 'Safety First', icon: <ShieldCheck className="w-4 h-4" />, color: 'text-amber-600 bg-amber-50' },
    { id: 'TECH_TALK', label: 'Rigging Intel', icon: <Zap className="w-4 h-4" />, color: 'text-purple-600 bg-purple-50' },
    { id: 'STORM_LOGS', label: 'Storm Ops', icon: <Wind className="w-4 h-4" />, color: 'text-slate-600 bg-slate-50' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Climber Animation Header */}
      <div className="relative bg-slate-900 rounded-[3rem] p-12 overflow-hidden h-64 flex items-center shadow-2xl">
        <div className="relative z-10 max-w-xl">
           <h2 className="text-4xl font-black text-white tracking-tight mb-4">Chatter from the Canopy</h2>
           <p className="text-emerald-400 font-bold text-lg">Rigging, felling, and shop talk with the crew.</p>
        </div>
        
        {/* Animated Climber SVG */}
        <div className="absolute top-0 right-32 h-full w-24 flex flex-col items-center">
          <div className="w-0.5 h-full bg-slate-700 dashed border-l border-dashed border-white/20"></div>
          <div className="absolute top-10 animate-bounce duration-[3000ms] ease-in-out">
             <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-xl shadow-emerald-900/50">
                <ArrowUp className="w-6 h-6 text-white rotate-180" />
             </div>
             <div className="mt-2 text-[8px] font-black text-emerald-400 text-center uppercase tracking-widest">Rappelling...</div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-slate-900 to-transparent"></div>
        <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-emerald-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar categories */}
        <div className="space-y-4">
           <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
             <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6">Crew Channels</h3>
             <div className="space-y-2">
               {categories.map(cat => (
                 <button key={cat.id} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-all text-left">
                   <div className="flex items-center gap-3">
                     <div className={`p-2 rounded-lg ${cat.color}`}>{cat.icon}</div>
                     <span className="text-sm font-bold text-slate-700">{cat.label}</span>
                   </div>
                   <span className="text-[10px] font-black text-slate-300">12+</span>
                 </button>
               ))}
             </div>
           </div>

           <div className="bg-emerald-600 rounded-[2rem] p-6 text-white text-center">
             <img src="https://i.ibb.co/LhygX9t/logo-monkey.png" className="w-16 h-16 mx-auto mb-4 brightness-0 invert" alt="Giddy Gibbon" />
             <p className="text-xs font-black uppercase tracking-widest text-emerald-200 mb-2">Safety Tip</p>
             <p className="text-sm font-bold leading-relaxed">"Never enter the drop zone without eye contact and a thumbs up."</p>
           </div>
        </div>

        {/* Post Feed */}
        <div className="lg:col-span-3 space-y-8">
           {/* Post Creator */}
           <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
             <div className="flex gap-4">
                <img src={currentUser.avatar} className="w-12 h-12 rounded-2xl border-2 border-emerald-500 object-cover" alt="Me" />
                <button 
                  onClick={() => setIsPosting(true)}
                  className="flex-1 text-left px-6 py-4 bg-slate-50 rounded-2xl text-slate-400 font-bold hover:bg-slate-100 transition-colors"
                >
                  Post a technical fell, gear review, or safety alert...
                </button>
             </div>
           </div>

           {/* Actual Posts */}
           <div className="flex justify-end gap-2 mb-4">
             <button
                onClick={() => setSortBy('recent')}
                className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-lg transition-all ${sortBy === 'recent' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}
             >
               Recent
             </button>
             <button
                onClick={() => setSortBy('trending')}
                className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-lg transition-all ${sortBy === 'trending' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}
             >
               Trending
             </button>
           </div>
           <div className="space-y-8">
             {sortedPosts.map(post => (
               <div key={post.id} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden group hover:shadow-xl transition-all">
                 <div className="p-8">
                   <div className="flex justify-between items-start mb-6">
                     <div className="flex items-center gap-4">
                       <img src={post.authorAvatar} className="w-12 h-12 rounded-2xl border border-slate-100 object-cover" alt={post.authorName} />
                       <div>
                         <h4 className="font-black text-slate-800 text-lg leading-tight flex items-center gap-2">
                           {post.authorName}
                           {!currentUser.following?.includes(post.authorId) && post.authorId !== currentUser.id && (
                             <button onClick={() => onFollow(post.authorId)} className="text-[10px] text-emerald-600 font-black uppercase tracking-widest hover:underline">+ Follow</button>
                           )}
                         </h4>
                         <p className="text-xs text-slate-400 font-bold">{new Date(post.timestamp).toLocaleDateString()} • {categories.find(c => c.id === post.category)?.label}</p>
                       </div>
                     </div>
                     <div className={`p-2 rounded-xl ${categories.find(c => c.id === post.category)?.color}`}>
                       {categories.find(c => c.id === post.category)?.icon}
                     </div>
                   </div>

                   <h3 className="text-xl font-black text-slate-800 mb-3 tracking-tight">{post.title}</h3>
                   <p className="text-slate-600 font-medium leading-relaxed mb-6 whitespace-pre-wrap">{post.content}</p>

                   {post.imageUrl && (
                     <div className="rounded-3xl overflow-hidden mb-6 bg-slate-100 aspect-video relative group/img">
                        <img src={post.imageUrl} className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105" alt="Post view" />
                        <div className="absolute inset-0 bg-black/10"></div>
                     </div>
                   )}

                   <div className="flex items-center gap-8 pt-6 border-t border-slate-50">
                     <button 
                      onClick={() => onLike(post.id)}
                      className={`flex items-center gap-2 font-black text-xs uppercase tracking-widest transition-all ${post.likes.includes(currentUser.id) ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}
                     >
                       <Heart className={`w-5 h-5 ${post.likes.includes(currentUser.id) ? 'fill-current' : ''}`} />
                       {post.likes.length} Likes
                     </button>
                     <button 
                       onClick={() => setCommentingOn(commentingOn === post.id ? null : post.id)}
                       className="flex items-center gap-2 font-black text-xs uppercase tracking-widest text-slate-400 hover:text-emerald-600 transition-all"
                     >
                       <MessageSquare className="w-5 h-5" />
                       {post.comments.length} Comments
                     </button>
                     <button className="flex items-center gap-2 font-black text-xs uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-all">
                       <Share2 className="w-5 h-5" />
                       Share
                     </button>
                   </div>

                   {/* Comment Section */}
                   {commentingOn === post.id && (
                     <div className="mt-8 pt-8 border-t border-slate-50 space-y-6 animate-in slide-in-from-top-4">
                        {post.comments.map(comment => (
                          <div key={comment.id} className="flex gap-3">
                            <img src={comment.authorAvatar} className="w-8 h-8 rounded-lg object-cover" alt={comment.authorName} />
                            <div className="flex-1 bg-slate-50 p-4 rounded-2xl">
                              <p className="text-xs font-black text-slate-800 mb-1">{comment.authorName}</p>
                              <p className="text-sm text-slate-600 font-medium">{comment.text}</p>
                            </div>
                          </div>
                        ))}
                        <form 
                          onSubmit={(e) => { e.preventDefault(); onComment(post.id, commentText); setCommentText(''); }}
                          className="flex gap-3"
                        >
                          <img src={currentUser.avatar} className="w-8 h-8 rounded-lg object-cover" alt="Me" />
                          <div className="flex-1 relative">
                            <input 
                              type="text" 
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              placeholder="Write a comment..."
                              className="w-full bg-slate-50 border-none rounded-xl px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 hover:text-emerald-700">
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </form>
                     </div>
                   )}
                 </div>
               </div>
             ))}
           </div>
        </div>
      </div>

      {/* Post Creator Modal */}
      {isPosting && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[300] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h2 className="text-2xl font-black text-slate-800">New Crew Post</h2>
              <button onClick={() => setIsPosting(false)} className="p-2 hover:bg-white rounded-full transition-all shadow-sm">
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>
            <form onSubmit={(e) => {
               e.preventDefault();
               onPost(newPost.title, newPost.content, newPost.category, newPost.imageUrl);
               setIsPosting(false);
            }} className="p-8 space-y-6">
               <div>
                 <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Channel</label>
                 <div className="grid grid-cols-3 gap-2">
                   {categories.map(cat => (
                     <button
                       key={cat.id}
                       type="button"
                       onClick={() => setNewPost({...newPost, category: cat.id as any})}
                       className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-all ${newPost.category === cat.id ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'}`}
                     >
                       {cat.icon}
                       <span className="text-[10px] font-black">{cat.label}</span>
                     </button>
                   ))}
                 </div>
               </div>
               <div>
                 <input 
                   required
                   type="text" 
                   placeholder="Headline..."
                   className="w-full text-xl font-black text-slate-800 placeholder:text-slate-300 border-none outline-none"
                   value={newPost.title}
                   onChange={e => setNewPost({...newPost, title: e.target.value})}
                 />
               </div>
               <div>
                 <textarea 
                   required
                   rows={5}
                   placeholder="What's happening in the canopy?"
                   className="w-full bg-slate-50 border-none rounded-2xl p-4 font-medium text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none"
                   value={newPost.content}
                   onChange={e => setNewPost({...newPost, content: e.target.value})}
                 />
               </div>
               <div className="flex gap-4">
                  <button type="button" className="flex items-center gap-2 px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-sm hover:bg-slate-200 transition-all">
                    <ImageIcon className="w-5 h-5" /> Attach Photo
                  </button>
                  <button type="submit" className="flex-1 bg-emerald-600 text-white px-6 py-4 rounded-2xl font-black text-sm hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-100 active:scale-95">
                    Launch Post
                  </button>
               </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const X = ({ className, ...props }: any) => (
  <svg className={className} {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
);
